import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Paper,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider
} from '@mui/material';
import { useData } from '../../context/DataContext';
import RichTextEditor from '../common/RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import DraggableList from '../common/DraggableList';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

const ManageBoardMembers = () => {
  const { state, dispatch } = useData();
  const { boardMembers } = state;

  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    image: '',
    bio: '',
    imageFile: null,
    imagePreview: null
  });

  const handleOpenDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name || '',
        position: member.position || '',
        image: member.image || '',
        bio: member.bio || '',
        imageFile: null,
        imagePreview: member.image || null  // Show existing image
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        position: '',
        image: '',
        bio: '',
        imageFile: null,
        imagePreview: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    // Cleanup image preview URL to prevent memory leaks (only for blob URLs)
    if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.imagePreview);
    }
    
    setOpenDialog(false);
    setEditingMember(null);
    setFormData({
      name: '',
      position: '',
      image: '',
      bio: '',
      imageFile: null,
      imagePreview: null
    });
  };

  // Cleanup function for image preview URLs
  useEffect(() => {
    return () => {
      if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.imagePreview);
      }
    };
  }, [formData.imagePreview]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Revoke old blob URL if exists
      if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.imagePreview);
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      setFormData({
        ...formData,
        imageFile: file,
        imagePreview: previewUrl
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const memberData = {
        name: formData.name,
        position: formData.position,
        bio: formData.bio,
        orderIndex: editingMember?.orderIndex ?? 0,
        isActive: editingMember?.isActive ?? true
      };

      let result;

      if (editingMember) {
        // Update existing member - spread the editingMember to preserve all properties including image_id
        result = await dispatch({
          type: 'UPDATE_BOARD_MEMBER',
          payload: {
            ...editingMember,
            ...memberData,
            imageId: editingMember.imageId || editingMember.image_id // Include existing image ID
          },
          imageFile: formData.imageFile
        });
      } else {
        // Add new member
        result = await dispatch({
          type: 'ADD_BOARD_MEMBER',
          payload: {
            ...memberData
          },
          imageFile: formData.imageFile
        });
      }

      if (result && result.success) {
        // Clean up preview URL
        if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(formData.imagePreview);
        }
        handleCloseDialog();
      } else {
        alert('Failed to save board member: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving board member:', error);
      alert('An error occurred while saving the board member: ' + error.message);
    }
  };

  const handleDeleteMember = async (memberToDelete) => {
    try {
      // Call the enhanced dispatch to delete from database
      const result = await dispatch({
        type: 'DELETE_BOARD_MEMBER',
        payload: memberToDelete.id
      });
      
      if (result && result.success) {
        // Success - the enhanced dispatch will update the state
        console.log('Board member deleted successfully');
      } else {
        alert('Failed to delete board member. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting board member:', error);
      alert('An error occurred while deleting the board member. Please try again.');
    }
  };

  const handleReorder = async (reorderedMembers) => {
    try {
      // Update local state immediately for instant UI feedback
      dispatch({
        type: 'REORDER_BOARD_MEMBERS',
        payload: reorderedMembers
      });
      
      // Then persist the new order in the database
      const updatePromises = reorderedMembers.map((member, index) => 
        dispatch({
          type: 'UPDATE_BOARD_MEMBER',
          payload: {
            ...member,
            orderIndex: index
          }
        })
      );
      
      const updateResults = await Promise.all(updatePromises);
      const failedUpdate = updateResults.find(result => !result?.success);
      if (failedUpdate) {
        throw new Error(failedUpdate.error || 'One or more board member updates failed');
      }

      console.log('Board members reordered successfully');
    } catch (error) {
      console.error('Error reordering board members:', error);
      alert('Failed to save new order. Please try again.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Board Members
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Board Member
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Add, edit, or remove board of directors members. Drag to reorder - changes will be reflected immediately on the public website.
      </Typography>

      {/* Board Members List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Board Members ({boardMembers?.length || 0})
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Drag items up or down to reorder
          </Typography>
        </Typography>
        
        {(!boardMembers || boardMembers.length === 0) ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No board members added yet. Click "Add Board Member" to get started.
            </Typography>
          </Box>
        ) : (
          <DraggableList
            items={boardMembers}
            onReorder={handleReorder}
            renderItem={(member, index, isDragging) => (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={member.image}
                      alt={member.name}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {member.name}
                      </Typography>
                      <Chip 
                        label={member.position} 
                        color="primary" 
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(member.bio) }}
                  />
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(member)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteMember(member)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            )}
          />
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMember ? 'Edit Board Member' : 'Add New Board Member'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Dr. Sarah Johnson"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
                placeholder="e.g., Chairperson & CEO"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Profile Image
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="board-member-image-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="board-member-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mr: 2 }}
                  >
                    Upload Image
                  </Button>
                </label>
                {formData.imageFile && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Selected: {formData.imageFile.name} ({Math.round(formData.imageFile.size / 1024)}KB)
                  </Typography>
                )}
                {formData.imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={formData.imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <RichTextEditor
                label="Biography"
                value={formData.bio}
                onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
                placeholder="Brief biography of the board member..."
                rows={4}
                helperText="Use the toolbar above to format your text with bold, italic, lists, and more"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={<SaveIcon />}
          >
            {editingMember ? 'Update Member' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageBoardMembers; 